import { CheckCircle2, CircleHelp, GitPullRequest, Rocket, XCircle } from 'lucide-react';
import type { WorkItemView } from '@vit/contracts';
import { Badge, EmptyState } from '@vit/ui';
import { useApiQuery } from '../../lib/api.js';
import { useTranslation } from '../../lib/i18n.js';
import { QueryState, SectionTitle, TimeAgo } from '../../components/common.js';
import { DeliveryBadge } from '../../components/badges.js';
import { DeliveryScopeEditor } from './delivery-scope-editor.js';

const coverageIcon = { COVERED: CheckCircle2, UNCOVERED: XCircle, UNKNOWN: CircleHelp } as const;
const coverageTone = { COVERED: 'success', UNCOVERED: 'neutral', UNKNOWN: 'warning' } as const;

/** Delivery scope, per-target coverage, pull requests and deployments for PRODUCTION work. */
export function DeliveryPanel({ item }: { item: WorkItemView }) {
  const { t } = useTranslation();
  const scope = useApiQuery('getDeliveryScope', { params: { id: item.id } }, { retry: false });
  const prs = useApiQuery('listPullRequests', { query: { workItemId: item.id } }, { retry: false });
  const deployments = useApiQuery('listDeployments', { query: { workItemId: item.id } }, { retry: false });
  return (
    <div className="flex flex-col gap-6">
      <QueryState query={scope}>
        {scope.data ? (
          <section>
            <SectionTitle actions={item.availableActions.includes('editDeliveryScope') ? <DeliveryScopeEditor item={item} scope={scope.data.data} /> : null}>
              {t(`status.scope.${scope.data.data.status}`)}
            </SectionTitle>
            <div className="mb-3 flex items-center gap-2">
              <DeliveryBadge state={scope.data.data.deliveryState} />
              {scope.data.data.confirmedBy ? (
                <span className="text-xs text-fg-subtle">
                  {scope.data.data.confirmationAuthority} · {scope.data.data.confirmedBy.displayName} · <TimeAgo iso={scope.data.data.confirmedAt} />
                </span>
              ) : null}
            </div>
            {scope.data.data.requirements.length === 0 ? (
              <EmptyState icon={<Rocket />} title={t('status.scope.AWAITING_SCOPE')} description={t('status.policyHelp.PRODUCTION')} className="py-6" />
            ) : (
              <ul className="flex flex-col gap-2">
                {scope.data.data.requirements.map((r) => {
                  const Icon = coverageIcon[r.coverage];
                  return (
                    <li key={r.id} className="rounded-[var(--radius)] border border-border px-3 py-2 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <Icon className="size-4 text-fg-subtle" />
                        <span className="font-medium">{r.service.name}</span>
                        <span className="text-fg-subtle">→ {r.environment.name}{r.targetSlot ? ` / ${r.targetSlot}` : ''}</span>
                        <Badge tone={coverageTone[r.coverage]} className="ml-auto">
                          {r.coverage}
                        </Badge>
                      </div>
                      {r.coverageReason ? <p className="mt-1 text-xs text-fg-subtle">{r.coverageReason}</p> : null}
                      <ul className="mt-1 flex flex-wrap gap-1">
                        {r.changes.map((c) => (
                          <li key={c.ref}>
                            <Badge tone={c.resolved ? 'neutral' : 'warning'}>{c.display}</Badge>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        ) : null}
      </QueryState>
      <section>
        <SectionTitle>Pull requests</SectionTitle>
        <QueryState query={prs} empty={prs.data?.data.length === 0 ? <p className="text-sm text-fg-subtle">—</p> : undefined}>
          <ul className="flex flex-col gap-1">
            {(prs.data?.data ?? []).map((pr) => (
              <li key={pr.id} className="flex items-center gap-2 text-sm">
                <GitPullRequest className="size-4 text-fg-subtle" />
                <span className="font-mono text-xs text-fg-subtle">
                  {pr.repository.fullName}#{pr.number}
                </span>
                <span className="min-w-0 flex-1 truncate">{pr.title}</span>
                <Badge tone={pr.state === 'MERGED' ? 'accent' : pr.state === 'OPEN' ? 'info' : 'neutral'}>{pr.state}</Badge>
                <Badge tone="outline">{t(`status.basis.${pr.basis}`)}</Badge>
              </li>
            ))}
          </ul>
        </QueryState>
      </section>
      <section>
        <SectionTitle>Deployments</SectionTitle>
        <QueryState query={deployments} empty={deployments.data?.data.length === 0 ? <p className="text-sm text-fg-subtle">—</p> : undefined}>
          <ul className="flex flex-col gap-1">
            {(deployments.data?.data ?? []).map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2 text-sm">
                <Rocket className="size-4 text-fg-subtle" />
                <span className="font-medium">{d.service.name}</span>
                <span className="text-fg-subtle">{d.environment.name}</span>
                <Badge tone={d.status === 'SUCCEEDED' ? 'success' : d.status === 'FAILED' ? 'danger' : 'neutral'}>{d.status}</Badge>
                <span className="font-mono text-xs text-fg-subtle">{(d.artifactDigest ?? d.commitSha ?? '').slice(0, 19)}</span>
                <span className="ml-auto text-xs">
                  <TimeAgo iso={d.completedAt ?? d.observedAt} />
                </span>
              </li>
            ))}
          </ul>
        </QueryState>
      </section>
    </div>
  );
}
